import styled from 'styled-components';
import Button from './Button';
import Input from './Input';
import Modal from './Modal';
import Select from './Select';
import { TrashIcon } from '@heroicons/react/24/outline';
import IconButton from './shared/IconButton';
import FormField from './shared/FormField';

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.9rem;
`;

const ModalHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  h2 {
    margin: 0;
    font-size: 1.25rem;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const CurrentPhoto = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
  position: relative;

  img {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 4px;
  }

  p {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.text};
    margin: 0;
  }
`;

const DeletePhotoButton = styled(IconButton)`
  position: absolute;
  top: -8px;
  left: -8px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ItemFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialData, 
  binName, 
  binNumber,
  mode,
  bins,
  onBinChange,
  onDeletePhoto,
  onDeleteReceipt
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader>
        <h2>{mode === 'edit' ? 'Edit Item' : 'Add Item'}</h2>
      </ModalHeader>
      <form onSubmit={onSubmit}>
        <FormGroup>
          <FormField
            type="text"
            name="itemName"
            id="itemName"
            defaultValue={initialData?.name || ''}
            label="Item Name"
            required
          />
        </FormGroup>

        <FormGroup>
          <FormField
            type="number"
            name="value"
            id="value"
            step="0.01"
            min="0"
            defaultValue={initialData?.value || ''}
            label="Item Value"
          />
        </FormGroup>

        <FormGroup>
          <Label>Location</Label>
          <Select
            name="binId"
            value={initialData?.binId || '0'}
            onChange={(e) => onBinChange?.(e.target.value)}
          >
            <option value="0">Room Items</option>
            {bins?.map(bin => (
              <option key={bin.id} value={bin.id}>
                Bin {bin.number}{bin.name ? ` (${bin.name})` : ''}
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Photo (optional)</Label>
          <Input
            type="file"
            name="photo"
            accept="image/*"
          />
          {initialData?.photoUrl && (
            <CurrentPhoto>
              <img src={initialData.photoUrl} alt="Current" />
              <DeletePhotoButton
                variant="danger"
                onClick={() => onDeletePhoto?.(initialData.photoUrl)}
              >
                <TrashIcon />
              </DeletePhotoButton>
              <p>Current photo will be replaced if you upload a new one</p>
            </CurrentPhoto>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Receipt (optional)</Label>
          <Input
            type="file"
            name="receipt"
            accept="image/*"
          />
          {initialData?.receiptPhotoUrl && (
            <CurrentPhoto>
              <img src={initialData.receiptPhotoUrl} alt="Current receipt" />
              <DeletePhotoButton
                variant="danger"
                onClick={() => onDeleteReceipt?.(initialData.receiptPhotoUrl)}
              >
                <TrashIcon />
              </DeletePhotoButton>
              <p>Current receipt will be replaced if you upload a new one</p>
            </CurrentPhoto>
          )}
        </FormGroup>

        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">
            {mode === 'edit' ? 'Save Changes' : 'Add Item'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default ItemFormModal; 